import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Printer, Edit, Tag, MapPin,
    Briefcase, User as UserIcon, Calendar,
    Smartphone, Hash, Barcode as BarcodeIcon,
    Building2, LayoutGrid, History as HistoryIcon,
    Save, Check
} from 'lucide-react';
import { AssetLabelModal } from './AssetLabelModal';
import { Asset, AssetCategory, User, CostCenter, Supplier, AssetHistoryEvent } from '../types';
import { api } from '../services/api';
import { formatDate, formatCurrency } from '../utils/formatters';

interface AssetDetailPanelProps {
    asset: Asset;
    onClose: () => void;
    onEdit: (asset: Asset) => void;
    categories: AssetCategory[];
    branches: User[];
    costCenters: CostCenter[];
    suppliers: Supplier[];
    team: User[];
    currentUser?: User;
    isAssetManager?: boolean;
    onAssetUpdated?: () => void;
    dateFormat?: string;
}

export const AssetDetailPanel: React.FC<AssetDetailPanelProps> = ({
    asset, onClose, onEdit, categories, branches, costCenters, suppliers, team, currentUser, isAssetManager, onAssetUpdated, dateFormat
}) => {
    const { t } = useTranslation('assets');
    const [history, setHistory] = React.useState<AssetHistoryEvent[]>([]);
    const [showLabelModal, setShowLabelModal] = React.useState(false);

    const [extraUserName, setExtraUserName] = React.useState<string | null>(null);
    const [extraManagerName, setExtraManagerName] = React.useState<string | null>(null);

    // Quick Edit State
    // const isAssetManager = currentUser?.memberProfile?.teamRole === 'asset_manager'; // Received from props now
    const [isEditing, setIsEditing] = React.useState(false);
    const [displayAsset, setDisplayAsset] = React.useState(asset);

    React.useEffect(() => {
        setDisplayAsset(asset);
    }, [asset]);

    const [editForm, setEditForm] = React.useState({
        branchId: asset.branchId || '',
        costCenterId: asset.costCenterId || '',
        assetUserId: asset.assetUserId || '',
        exactLocation: asset.exactLocation || ''
    });
    const [isSaving, setIsSaving] = React.useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await api.updateAsset(asset.id, editForm, currentUser?.id, currentUser?.memberProfile?.name || currentUser?.displayName);

            // Update local display immediately
            setDisplayAsset(prev => ({ ...prev, ...editForm }));

            setIsEditing(false);
            if (onAssetUpdated) onAssetUpdated();
        } catch (error) {
            console.error("Failed to update asset", error);
            alert("Failed to update asset");
        } finally {
            setIsSaving(false);
        }
    };

    React.useEffect(() => {
        const fetchHistory = async () => {
            if (asset.id) {
                const h = await api.getAssetHistory(asset.id);
                setHistory(h);
            }
        };
        fetchHistory();

        // Check if we need to fetch user names that are not in the restricted lists
        const resolveUser = async (id: string, setter: (name: string) => void) => {
            // Check local lists first
            const inTeam = team.find(u => u.id === id);
            if (inTeam) return;
            const inBranches = branches.find(u => u.id === id);
            if (inBranches) return;

            // Not found locally? Fetch individual
            try {
                const u = await api.getUser(id);
                const name = u.memberProfile?.name || u.displayName || u.email || id;
                setter(name);
            } catch (e) {
                console.warn(`Failed to resolve user name for ${id}`, e);
            }
        };

        if (asset.assetUserId) resolveUser(asset.assetUserId, setExtraUserName);
        if (asset.assetManagerId) resolveUser(asset.assetManagerId, setExtraManagerName);

    }, [asset.id, asset.assetUserId, asset.assetManagerId]);



    const getCategoryInfo = (id: string) => {
        const cat = categories.find(c => c.id === id);
        if (!cat) return { title: id };
        if (cat.parentId) {
            const parent = categories.find(p => p.id === cat.parentId);
            return parent
                ? { title: parent.title, subTitle: cat.title }
                : { title: cat.title };
        }
        return { title: cat.title };
    };
    const getBranchName = (id: string) => {
        const b = branches.find(b => b.id === id);
        return b ? (b.profile?.companyName || b.config?.companyName || b.displayName || b.email) : id;
    };
    const getCostCenterTitle = (id: string) => costCenters.find(c => c.id === id)?.title || '-';
    // const getSupplierName = (id: string) => suppliers.find(s => s.id === id)?.name || '-';
    const getTeamMemberName = (id: string, extraName?: string | null) => {
        if (extraName) return extraName;
        const m = team.find(u => u.id === id);
        if (m) return m.memberProfile?.name || m.displayName || m.email;
        const b = branches.find(u => u.id === id);
        return b ? (b.profile?.companyName || b.config?.companyName || b.displayName || b.email) : '-';
    };

    const statusColors = {
        active: 'bg-green-100 text-green-700',
        temporarily_inactive: 'bg-orange-100 text-orange-700',
        inactive: 'bg-slate-100 text-slate-600',
        deleted: 'bg-red-100 text-red-700',
        sold: 'bg-blue-100 text-blue-700',
        written_off: 'bg-gray-100 text-gray-700',
        fully_depreciated: 'bg-yellow-100 text-yellow-800',
        for_sale: 'bg-purple-100 text-purple-700',
        cancelled: 'bg-red-50 text-red-600',
        to_be_destroyed: 'bg-red-100 text-red-800',
        to_be_moved: 'bg-blue-50 text-blue-600'
    };

    return (
        <div className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl z-50 overflow-y-auto flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
            {/* Header */}
            <div className="p-8 border-b border-slate-100 sticky top-0 bg-white/95 backdrop-blur z-10">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${statusColors[asset.status as keyof typeof statusColors] || 'bg-slate-100'}`}>
                            {t(`status.${asset.status}`)}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 font-mono">
                            {asset.code}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition">
                        <X size={24} />
                    </button>
                </div>

                <h2 className="text-2xl font-black text-slate-900 mb-2">{asset.description}</h2>
                <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                    <span className="flex items-center gap-1.5"><Tag size={14} /> {asset.code}</span>
                    <span className="flex items-center gap-1.5"><Building2 size={14} /> {getBranchName(asset.branchId)}</span>
                </div>
            </div>

            <div className="p-8 space-y-8 flex-1">
                {/* Financial Card */}
                <div className="bg-[#1A202C] rounded-2xl p-6 text-white shadow-xl shadow-slate-200">
                    <div className="flex justify-between items-start mb-6 border-b border-slate-700 pb-4">
                        <div>
                            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                {t('wizard.step_2.total_value').toUpperCase()}
                            </span>
                            <div className="text-3xl font-black tracking-tight">
                                {formatCurrency(asset.totalValue || 0, 'EUR')}
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                {t('wizard.step_2.acquisition_date').toUpperCase()}
                            </span>
                            <div className="text-xl font-bold font-mono">
                                {formatDate(asset.acquisitionDate, dateFormat)}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                        <div>
                            <span className="block text-[10px] font-bold text-slate-500 mb-1">{t('wizard.step_2.quantity')}</span>
                            <div className="font-bold text-xl text-white">{asset.quantity}</div>
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-slate-500 mb-1">{t('wizard.step_2.unit_value')}</span>
                            <div className="font-bold">{formatCurrency(asset.unitValue || 0, 'EUR')}</div>
                        </div>
                        <div className="text-right">
                            <span className="block text-[10px] font-bold text-green-400 mb-1">DEPRECIATION</span>
                            <div className="font-bold text-green-400">
                                {/** Placeholder calculation if needed */}
                                {asset.residualValue ? `${asset.residualValue} €` : '-'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Identity Section */}
                <div>
                    <h3 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-2">
                        <LayoutGrid size={16} /> {t('wizard.step_5.section_identity')}
                    </h3>
                    <div className="grid grid-cols-2 gap-y-8 gap-x-4">
                        <DetailItem label={t('wizard.step_1.description')} value={asset.supplierDescription || asset.description} />
                        <DetailItem label={t('wizard.step_1.manufacturer')} value={asset.manufacturer} />
                        {(() => {
                            const info = getCategoryInfo(asset.categoryId);
                            return <DetailItem label={t('wizard.step_1.category')} value={info.title} subValue={info.subTitle} />;
                        })()}
                        <DetailItem label={t('wizard.step_1.model')} value={asset.model} />
                        <DetailItem label={t('wizard.step_1.serial')} value={asset.serialNumber} />
                        <DetailItem label={t('wizard.step_1.barcode')} value={asset.barcode} icon={<BarcodeIcon size={14} className="text-slate-400" />} />
                    </div>
                </div>

                {/* Location Section */}
                <div>
                    <h3 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-2">
                        <MapPin size={16} /> {t('wizard.step_5.section_location')}
                    </h3>

                    <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            {isEditing ? (
                                <div className="col-span-1">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t('wizard.step_4.branch')}</label>
                                    <select
                                        value={editForm.branchId}
                                        onChange={e => setEditForm(prev => ({ ...prev, branchId: e.target.value }))}
                                        className="w-full text-sm border-slate-200 rounded-lg"
                                    >
                                        <option value="">-</option>
                                        {branches.map(b => (
                                            <option key={b.id} value={b.id}>
                                                {b.profile?.companyName || b.config?.companyName || b.displayName || b.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <DetailItem label={t('wizard.step_4.branch')} value={getBranchName(displayAsset.branchId)} />
                            )}

                            {isEditing ? (
                                <div className="col-span-1">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t('wizard.step_4.cost_center')}</label>
                                    <select
                                        value={editForm.costCenterId}
                                        onChange={e => setEditForm(prev => ({ ...prev, costCenterId: e.target.value }))}
                                        className="w-full text-sm border-slate-200 rounded-lg"
                                    >
                                        <option value="">-</option>
                                        {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.title}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <DetailItem label={t('wizard.step_4.cost_center')} value={getCostCenterTitle(displayAsset.costCenterId)} />
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <DetailItem
                                label={t('wizard.step_4.asset_manager')}
                                value={getTeamMemberName(asset.assetManagerId, extraManagerName)}
                                icon={<div className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-bold">M</div>}
                            />
                            {isEditing ? (
                                <div className="col-span-1">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t('wizard.step_4.asset_user')}</label>
                                    <select
                                        value={editForm.assetUserId}
                                        onChange={e => setEditForm(prev => ({ ...prev, assetUserId: e.target.value }))}
                                        className="w-full text-sm border-slate-200 rounded-lg"
                                    >
                                        <option value="">-</option>
                                        {team
                                            .filter(m => m.memberProfile?.teamRole === 'asset_user' || m.memberProfile?.teamRole === 'asset_manager')
                                            .map(m => (
                                                <option key={m.id} value={m.id}>
                                                    {m.memberProfile?.name || m.displayName || m.email}
                                                </option>
                                            ))}
                                        {/* If current Asset User is NOT in team list (e.g. from branch or external), keep option */}
                                        {displayAsset.assetUserId && !team.find(u => u.id === displayAsset.assetUserId) && (
                                            <option value={displayAsset.assetUserId}>
                                                {extraUserName || displayAsset.assetUserId}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            ) : (
                                <DetailItem
                                    label={t('wizard.step_4.asset_user')}
                                    value={getTeamMemberName(displayAsset.assetUserId, extraUserName)}
                                    icon={<div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold">U</div>}
                                />
                            )}
                        </div>
                        <div className="pt-4 border-t border-slate-200">
                            {isEditing ? (
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t('wizard.step_4.exact_location')}</label>
                                    <input
                                        type="text"
                                        value={editForm.exactLocation}
                                        onChange={e => setEditForm(prev => ({ ...prev, exactLocation: e.target.value }))}
                                        className="w-full text-sm border-slate-200 rounded-lg"
                                    />
                                </div>
                            ) : (
                                <DetailItem
                                    label={t('wizard.step_4.exact_location')}
                                    value={displayAsset.exactLocation}
                                    icon={<MapPin size={14} className="text-slate-400" />}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* History Section */}
                <div>
                    <h3 className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-2">
                        <HistoryIcon size={16} /> {t('history.title')}
                    </h3>
                    <div className="space-y-6 relative pl-4 border-l-2 border-slate-100 ml-2">
                        {history.map((event, idx) => (
                            <div key={event.id || idx} className="relative">
                                <div className="absolute -left-[25px] top-0 w-4 h-4 rounded-full border-[3px] border-blue-100 bg-blue-500 box-content"></div>
                                <div className="flex justify-between items-start mb-1">
                                    <h4 className="font-bold text-slate-800 text-sm">
                                        {t(`history.${event.actionType}`) || event.description}
                                    </h4>
                                    <span className="text-[10px] font-bold text-slate-400">
                                        {new Date(event.createdAt).toLocaleDateString('el-GR')}
                                    </span>
                                </div>

                                {/* Changes Detail */}
                                {event.changes && event.changes.length > 0 && (
                                    <div className="mb-2 space-y-1">
                                        {event.changes.map((change, i) => {
                                            const formatValue = (field: string, val: any) => {
                                                if (!val) return '-';
                                                if (field === 'status') return t(`status.${val}`);
                                                if (field === 'branchId') return getBranchName(val);
                                                if (field === 'costCenterId') return getCostCenterTitle(val);
                                                if (field === 'costCenterId') return getCostCenterTitle(val);
                                                if (field === 'assetManagerId' || field === 'assetUserId') return getTeamMemberName(val, field === 'assetUserId' ? extraUserName : extraManagerName);
                                                return val;
                                                return val;
                                            };

                                            return (
                                                <div key={i} className="text-xs text-slate-500 font-mono">
                                                    {formatValue(change.field, change.oldValue)} {'->'} {formatValue(change.field, change.newValue)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    {event.userName || t('history.unknown_user')}
                                </div>
                            </div>
                        ))}
                        {history.length === 0 && (
                            <div className="text-sm text-slate-400 italic py-4">
                                No history available.
                            </div>
                        )}
                    </div>
                </div>
            </div>


            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 sticky bottom-0 flex gap-4">
                <button
                    onClick={() => isEditing ? setIsEditing(false) : setShowLabelModal(true)}
                    className={`flex-1 ${isEditing ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'} py-3 rounded-xl font-bold transition flex items-center justify-center gap-2`}
                >
                    {isEditing ? <><X size={18} /> {t('actions.cancel')}</> : <><Printer size={18} /> {t('actions.print_label', 'Ετικέτα / QR')}</>}
                </button>
                <button
                    onClick={() => {
                        if (isEditing) {
                            handleSave();
                        } else if (isAssetManager) {
                            setIsEditing(true);
                        } else {
                            onEdit(asset);
                        }
                    }}
                    disabled={isSaving}
                    className={`flex-1 ${isEditing ? 'bg-green-600 hover:bg-green-700' : 'bg-[#0088CC] hover:bg-[#0077B3]'} text-white py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 shadow-lg shadow-blue-100`}
                >
                    {isEditing ? (
                        <>{isSaving ? t('wizard.submitting_btn', 'Saving...') : <><Save size={18} /> {t('actions.save')}</>}</>
                    ) : (
                        <><Edit size={18} /> {isAssetManager ? t('actions.quick_edit', 'Quick Edit') : t('actions.edit')}</>
                    )}
                </button>
            </div>

            {
                showLabelModal && (
                    <AssetLabelModal
                        asset={asset}
                        onClose={() => setShowLabelModal(false)}
                        dateFormat={dateFormat}
                    />
                )
            }
        </div >
    );
};

const DetailItem = ({ label, value, subValue, icon }: { label: string, value?: React.ReactNode, subValue?: string, icon?: React.ReactNode }) => (
    <div>
        <span className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{label}</span>
        <div className="flex items-start gap-2">
            {icon && <div className="mt-0.5">{icon}</div>}
            <div className="flex flex-col min-w-0">
                <span className="font-bold text-slate-800 text-sm truncate block">{value || '-'}</span>
                {subValue && <span className="text-xs text-slate-500 truncate block">{subValue}</span>}
            </div>
        </div>
    </div>
);
